import { useCallback, useEffect, useRef, useState } from 'react';
import { useClient } from 'shared/apollo';
import { removeConnections } from 'shared/apollo/client';
import { getUserPrograms } from './api';
import { PAGE_SIZE } from './constants';
import { GetUserProgramsVars, UserProgram } from './types';

// The GraphQL response nests report allocations in { edges: [{ node }] }.
// Flatten to match the UserProgramReport type used throughout the UI.
const normalizePrograms = (programs: UserProgram[]): UserProgram[] =>
  programs.map(p => ({
    ...p,
    reports: p.reports.map(r => ({
      ...r,
      allocations:
        removeConnections((r as { allocations: unknown }).allocations) || [],
      results:
        removeConnections(((r as unknown) as { results: unknown }).results) ||
        [],
    })),
  }));

type Status =
  | 'idle'
  | 'loading-initial'
  | 'loading-more'
  | 'refreshing'
  | 'success'
  | 'error';

export interface InfiniteProgramsState {
  programs: UserProgram[];
  totalCount: number;
  status: Status;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  retry: () => void;
  /** Refetch the list from offset 0, replacing local state. */
  refetch: () => void;
  /** Optimistically update a program in local state without refetching. */
  updateProgramLocal: (
    programId: number,
    updater: (p: UserProgram) => UserProgram,
  ) => void;
  /** Prepend a newly-created program to the top of the list. */
  prependProgram: (p: UserProgram) => void;
  /** Remove a program from local state (e.g. after disableProgram succeeds). */
  removeProgramLocal: (programId: number) => void;
}

type Filters = Pick<GetUserProgramsVars, 'search' | 'status' | 'stage'>;

export const useInfinitePrograms = (
  filters: Filters,
): InfiniteProgramsState => {
  const client = useClient();
  const [programs, setPrograms] = useState<UserProgram[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [offset, setOffset] = useState(0);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const requestSeqRef = useRef(0);

  const fetchPage = useCallback(
    async (nextOffset: number, replace: boolean) => {
      if (!client) return;
      const seq = ++requestSeqRef.current;
      setStatus(replace ? 'loading-initial' : 'loading-more');
      setError(null);
      try {
        const res = await getUserPrograms(client, {
          ...filters,
          first: PAGE_SIZE,
          offset: nextOffset,
        });
        if (seq !== requestSeqRef.current) return;
        const payload = res.data?.getUserPrograms;
        if (!payload?.success) {
          setStatus('error');
          setError('Unable to load programs.');
          return;
        }
        setTotalCount(payload.totalCount);
        const nextPrograms = normalizePrograms(payload.programs);
        setPrograms(prev =>
          replace ? nextPrograms : [...prev, ...nextPrograms],
        );
        setOffset(nextOffset + nextPrograms.length);
        setStatus('success');
      } catch (e) {
        if (seq !== requestSeqRef.current) return;
        setStatus('error');
        setError((e as Error)?.message || 'Unable to load programs.');
      }
    },
    [client, filters.search, filters.status, filters.stage],
  );

  // Refetch from scratch when filters change.
  useEffect(() => {
    setPrograms([]);
    setOffset(0);
    setTotalCount(0);
    fetchPage(0, true);
  }, [filters.search, filters.status, filters.stage, client]);

  const loadMore = useCallback(() => {
    if (status === 'loading-initial' || status === 'loading-more') return;
    if (programs.length >= totalCount) return;
    fetchPage(offset, false);
  }, [fetchPage, offset, programs.length, totalCount, status]);

  const retry = useCallback(() => {
    fetchPage(programs.length === 0 ? 0 : offset, programs.length === 0);
  }, [fetchPage, offset, programs.length]);

  // Silent refresh: keep existing rows visible until the new page arrives,
  // then swap them in place. Avoids the "list flashes empty and re-fills"
  // flicker that a full reset causes when we already have data.
  const refetch = useCallback(async () => {
    if (!client) return;
    const seq = ++requestSeqRef.current;
    setStatus(prev => (prev === 'success' ? 'refreshing' : 'loading-initial'));
    setError(null);
    try {
      const res = await getUserPrograms(client, {
        ...filters,
        first: PAGE_SIZE,
        offset: 0,
      });
      if (seq !== requestSeqRef.current) return;
      const payload = res.data?.getUserPrograms;
      if (!payload?.success) {
        setStatus('error');
        setError('Unable to load programs.');
        return;
      }
      const nextPrograms = normalizePrograms(payload.programs);
      setPrograms(nextPrograms);
      setTotalCount(payload.totalCount);
      setOffset(nextPrograms.length);
      setStatus('success');
    } catch (e) {
      if (seq !== requestSeqRef.current) return;
      setStatus('error');
      setError((e as Error)?.message || 'Unable to load programs.');
    }
  }, [client, filters.search, filters.status, filters.stage]);

  const updateProgramLocal = useCallback(
    (programId: number, updater: (p: UserProgram) => UserProgram) => {
      setPrograms(prev =>
        prev.map(p => (p.program.dbId === programId ? updater(p) : p)),
      );
    },
    [],
  );

  const prependProgram = useCallback((p: UserProgram) => {
    setPrograms(prev => [p, ...prev]);
    setTotalCount(c => c + 1);
  }, []);

  const removeProgramLocal = useCallback((programId: number) => {
    setPrograms(prev => {
      const next = prev.filter(p => p.program.dbId !== programId);
      if (next.length !== prev.length) {
        setTotalCount(c => Math.max(0, c - 1));
        setOffset(o => Math.max(0, o - 1));
      }
      return next;
    });
  }, []);

  return {
    programs,
    totalCount,
    status,
    error,
    hasMore: programs.length < totalCount,
    loadMore,
    retry,
    refetch,
    updateProgramLocal,
    prependProgram,
    removeProgramLocal,
  };
};
